<script setup lang="ts">
import { computed, onMounted, ref, watchEffect } from 'vue'
import { useOidcAuth, useRoute } from '#imports'
import AuthGateShellSkeleton from '~/components/app/AuthGateShellSkeleton.vue'
import SourceButton from '~/components/ui/SourceButton.vue'
import { normalizeAuthRedirectPath, setPendingLoginToast, storeAuthRedirectTarget } from '~/utils/authRedirect'

interface AuthGateProps {
  title?: string
  description?: string
  autoFetch?: boolean
}

const props = withDefaults(defineProps<AuthGateProps>(), {
  title: 'Bienvenue sur MonOrga',
  description: 'Vous devez être connecté via Gardian WebSSO pour accéder à l’organisation.',
  autoFetch: true
})

const { loggedIn, login, fetch } = useOidcAuth()
const route = useRoute()

function resolveLoginTargetPath(
  redirectFromQuery: string | null,
  currentPath: string | null,
  routePath: string
): string | null {
  if (redirectFromQuery && !redirectFromQuery.startsWith('/login')) return redirectFromQuery
  if (!routePath.startsWith('/login')) return currentPath
  return null
}

/**
 * En mode autoFetch, on démarre en « synchronisation » pour que le SSR et le premier rendu client
 * affichent le même squelette que celui après onMounted. Sinon le SSR rendait le slot (contenu)
 * puis le client passait au skeleton → mismatch d’hydratation (NuxtLink, AuthGate, etc.).
 */
const isSyncing = ref(props.autoFetch)
const didTryFetch = ref(false)

const showLoginPanel = computed(() => {
  if (!props.autoFetch) return !loggedIn.value
  if (!didTryFetch.value) return false
  return !loggedIn.value
})

const slotHidden = computed(() => {
  if (props.autoFetch && isSyncing.value) return true
  return showLoginPanel.value
})

onMounted(async () => {
  if (!props.autoFetch) return

  try {
    await fetch()
  } catch (e) {
    // on ignore : on basculera en login panel
    console.warn('[AuthGate] OIDC fetch failed', e)
  } finally {
    didTryFetch.value = true
    isSyncing.value = false
  }
})

const handleLogin = async () => {
  try {
    const queryRedirectTo = route.query.monorga_return_to
    const redirectFromQuery = normalizeAuthRedirectPath(
      typeof queryRedirectTo === 'string' ? queryRedirectTo : null
    )
    const currentPath = normalizeAuthRedirectPath(route.fullPath)
    const targetPath = resolveLoginTargetPath(redirectFromQuery, currentPath, route.path)

    if (targetPath) {
      storeAuthRedirectTarget(targetPath)
      setPendingLoginToast()
      await login()
      return
    }

    setPendingLoginToast()
    await login()
  } catch (e) {
    console.error('[AuthGate] login failed', e)
  }
}

watchEffect(() => {
  if (!showLoginPanel.value) return
  if (route.path !== '/login') return
  void handleLogin()
})
</script>

<template>
  <div class="auth-gate">
    <AuthGateShellSkeleton
      v-if="autoFetch && isSyncing"
      class="auth-gate__overlay"
    />

    <div v-if="showLoginPanel" class="login-panel">
      <h1 class="login-title">{{ title }}</h1>
      <p class="login-text">{{ description }}</p>

      <SourceButton
        text="Se connecter"
        size="medium"
        type="primary"
        @click="handleLogin"
      />
    </div>

    <!--
      Slot toujours monté (masqué pendant sync / login) pour que Nuxt enregistre <NuxtPage />
      et éviter l’avertissement « NuxtPage has not been used » + erreurs d’hydratation.
    -->
    <div
      class="auth-gate__page"
      :hidden="slotHidden"
      :aria-hidden="slotHidden"
    >
      <slot />
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/elpdl/spacing" as *;

.auth-gate {
  position: relative;
}

.auth-gate__overlay {
  position: relative;
  z-index: 1;
}

.auth-gate__page[hidden] {
  display: none !important;
}

.login-panel {
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: $spacing-m;
  text-align: center;
}

.login-title {
  margin: 0;
  font-size: $spacing-xl;
}

.login-text {
  margin: 0;
  opacity: 0.8;
  max-width: 520px;
}
</style>
